import { useFindMany } from "@gadgetinc/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PauseIcon,
  PencilSquareIcon,
  PlayCircleIcon,
} from "@heroicons/react/16/solid";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Icon, Tooltip } from "@shopify/polaris";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import ShareModal from "../../externalComponents/ShareModal";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { CustomerContext } from "../../providers";
import {
  getAutomationStatus,
  getAutomationTypeIcon,
} from "./helpers/functions";
import AutomationActionsDropdown from "./shared/AutomationActionsDropdown";
import DialogArchive from "./shared/DialogArchive";
import DialogDuplicate from "./shared/DialogDuplicate";
import { mappingAutomationTypeWithField } from "./shared/mapping-automation-type-with-field.const";
import { mappingAutomationTypeWithName } from "./shared/mapping-automation-type-with-name.const";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { BannerNewLibraryFlow } from "../../externalComponents/BannerNewLibraryFlow";
import { getPromotedLibraryFlows } from "../../functions/getPromotedLibraryFlows";

const AutomationsPage = () => {
  const { user, customer, integrations } = useContext(CustomerContext);
  const hasTurnover = integrations.shopify || integrations.splio;
  const isMobile = useIsMobile();

  const [listData, setListData] = useState({
    selectedCampaign: null,
    isArchiveLoading: false,
    isDuplicateLoading: false,
    isModalOpen: false,
    isModalDuplicateOpen: false,
    isModalShareOpen: false,
  });
  const [search, setSearch] = useState("");

  const updateIsModalShareOpen = useCallback((isModalShareOpen) => {
    setListData((_listData) => ({
      ..._listData,
      isModalShareOpen,
    }));
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const NUM_ON_PAGE = 10;

  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  const [hasFetchedAllData, setHasFetchedAllData] = useState(false);

  // URL parameter handling states
  const [targetPageFromUrl, setTargetPageFromUrl] = useState(null);
  const [isNavigatingFromUrl, setIsNavigatingFromUrl] = useState(false);
  const [isLoadingToTargetPage, setIsLoadingToTargetPage] = useState(false);
  const _defaultPartialFields = {
    id: true,
    name: true,
    updatedAt: true,
    trigger: true,
    automationType: true,
    isAutomationActive: true,
    scenario: {
      id: true,
    },
    customer: {
      abandonedCartCampaignId: true,
      backInStockCampaignId: true,
      orderConfirmationCampaignId: true,
      orderTrackingCampaignId: true,
      optinConfirmationCampaignId: true,
    },
  };
  const [fields, setFields] = useState(_defaultPartialFields);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [
      { customerId: { equals: user.customerId } },
      { trigger: { equals: "api" } },
      { isArchived: { notEquals: true } },
    ],
  });

  const [{ data: searchData }] = useFindMany(api.campaign, {
    search,
    select: { id: true },
  });

  useEffect(() => {
    if (search) {
      if (searchData) {
        const ids = searchData.map(({ id }) => id);
        setCriteria({
          filter: [
            { customerId: { equals: user.customerId } },
            { id: { in: ids.length > 0 ? ids : [0] } },
            { trigger: { equals: "api" } },
            { isArchived: { notEquals: true } },
          ],
        });
        setFields(_defaultPartialFields);
        setHasFetchedAllData(false);
      }
    } else {
      setCriteria({
        ...cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { trigger: { equals: "api" } },
          { isArchived: { notEquals: true } },
        ],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    }
  }, [searchData, search]);

  // Initialize page number from URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;

    if (initialPage > 1) {
      setTargetPageFromUrl(initialPage);
      setIsNavigatingFromUrl(true);
      setIsLoadingToTargetPage(true);
    } else {
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [location.search]);

  const [{ data, fetching }] = useFindMany(api.campaign, {
    ...criteria,
    select: fields,
    sort: [
      //{ isAutomationActive: "Descending" }, // TODO trier dans le bon ordre quand tout sera en actif ou inactif
      { updatedAt: "Descending" },
    ],
  });

  useEffect(() => {
    if (!fetching && !hasFetchedAllData) {
      setFields({
        ..._defaultPartialFields,
        sent: true,
        delivered: true,
        read: true,
        attributedRevenueShopify: true,
        attributedOrdersShopify: true,
      });
      setHasFetchedAllData(true);
    }
  }, [data]);

  const changePage = useCallback(
    (_cursor, _pageNumber) => {
      setCursor(_cursor);
      setPageNumber(_pageNumber);

      // Only update URL parameter if we're not navigating from URL
      if (!isNavigatingFromUrl) {
        const searchParams = new URLSearchParams(location.search);
        if (_pageNumber === 1) {
          searchParams.delete("page");
        } else {
          searchParams.set("page", _pageNumber.toString());
        }

        const newSearch = searchParams.toString();
        const newUrl = newSearch
          ? `${location.pathname}?${newSearch}`
          : location.pathname;
        navigate(newUrl, { replace: true });
      }

      setCriteria({
        ..._cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { trigger: { equals: "api" } },
          { isArchived: { notEquals: true } },
        ],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    },
    [
      location.search,
      location.pathname,
      navigate,
      user.customerId,
      isNavigatingFromUrl,
    ],
  );

  const getNextPage = () => {
    if (data?.endCursor) {
      changePage({ first: NUM_ON_PAGE, after: data.endCursor }, pageNumber + 1);
    }
  };

  // Handle navigation to correct page from URL parameter
  useEffect(() => {
    if (
      targetPageFromUrl &&
      targetPageFromUrl > 1 &&
      data &&
      data.length > 0 &&
      pageNumber < targetPageFromUrl &&
      !fetching
    ) {
      // We have data and need to navigate to the target page
      // Call getNextPage to move to the next page
      if (data?.endCursor) {
        getNextPage();
      }
    } else if (targetPageFromUrl && pageNumber === targetPageFromUrl) {
      // We've reached the target page, clear the target and navigation flag
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [data, targetPageFromUrl, pageNumber, fetching]);

  const getPreviousPage = () => {
    if (pageNumber > 1) {
      changePage(
        { last: NUM_ON_PAGE, before: data.startCursor },
        pageNumber - 1,
      );
    }
  };

  const computedData = useMemo(() => {
    return (data || []).map((item) => {
      const computeARPC = (_automation) => {
        const {
          delivered,
          attributedRevenueShopify,
          sent,
          externalTurnover,
          attributedOrdersShopify,
        } = _automation;
        console.log(
          delivered && attributedOrdersShopify > 0 ? (
            <div className="text-xxs font-bold leading-none">
              {attributedOrdersShopify} sale
              {attributedOrdersShopify > 1 ? "s" : ""}
            </div>
          ) : null,
        );

        if (!sent) return "-";

        if (attributedRevenueShopify && sent) {
          return (attributedRevenueShopify / sent).toFixed(2);
        }
        if (externalTurnover > 0) {
          return (externalTurnover / sent).toFixed(2);
        }
        return "-";
      };
      const { automationType } = item;
      const automationTypeIcon = getAutomationTypeIcon(automationType);
      const { isAutomationActive } = getAutomationStatus(item) || {
        isAutomationActive: false,
      };
      const arpc = computeARPC(item);
      const computeARPCDetails = (_arpc) => {
        let arpcLabel = "Great";
        let arpcColor = "lime";
        if (_arpc > 0 && _arpc < 4) {
          arpcLabel = "Fair";
          arpcColor = "indigo";
        } else if (_arpc >= 4 && _arpc < 8) {
          arpcLabel = "Good";
          arpcColor = "sky";
        }
        return { arpcColor, arpcLabel };
      };
      const { arpcColor, arpcLabel } = computeARPCDetails(arpc);

      return {
        ...item,
        automationTypeIcon,
        isAutomationActive,
        arpc,
        arpcColor,
        arpcLabel,
      };
    });
  }, [data, hasFetchedAllData]);

  const [dataLibraryFlowAutomations, setDataLibraryFlowAutomations] = useState(
    [],
  );

  useEffect(() => {
    const loadPromotedFlows = async () => {
      try {
        const automations = await getPromotedLibraryFlows({
          isAutomation: true,
        });

        setDataLibraryFlowAutomations(automations);
      } catch (error) {
        console.error("Erreur lors du chargement des flows promus:", error);
      }
    };

    loadPromotedFlows();
  }, []);

  return (
    <>
      <PageHeading
        title="Automations"
        searchPlaceholder="Search in automations"
        search={search}
        setSearch={setSearch}
        iconPrimaryAction={<PlusIcon />}
        {...(!isMobile && {
          primaryAction: "Create",
          handlePrimaryAction: async () => {
            navigate("/automations/new");
          },
        })}
      />
      {isLoadingToTargetPage && <LoadingSpinner />}
      <OverflowFrame>
        {!isMobile && (
          <div className="mt-4">
            <BannerNewLibraryFlow
              dataLibraryFlow={dataLibraryFlowAutomations}
              hasAutomations={true}
              title="Recommended automations"
              user={user}
            />
          </div>
        )}
        {computedData && !isLoadingToTargetPage && (
          <>
            {computedData.length > 0 ? (
              <Table className="mx-3 md:mx-0">
                <TableHead>
                  <TableRow>
                    <TableHeader
                      className={`w-[${hasTurnover ? "20" : "50"}%]`}
                    >
                      Name
                    </TableHeader>
                    <TableHeader className="text-center">Status</TableHeader>
                    <TableHeader className="text-center">Type</TableHeader>
                    {!isMobile && (
                      <>
                        <TableHeader className="text-center">
                          Last edited
                        </TableHeader>
                        <TableHeader className="text-center">
                          Sendings
                        </TableHeader>
                        {hasTurnover && (
                          <>
                            <TableHeader className="text-center">
                              Turnover
                            </TableHeader>
                            <TableHeader className="text-center">
                              Performance
                            </TableHeader>
                            <TableHeader className="text-center">
                              ARPC
                            </TableHeader>
                          </>
                        )}
                        <TableHeader className="text-center">
                          Actions
                        </TableHeader>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {computedData.map((automation) => {
                    const {
                      id,
                      name,
                      automationType,
                      updatedAt,
                      sent,
                      read,
                      attributedRevenueShopify,
                      externalTurnover,
                      attributedOrdersShopify,
                      delivered,
                      arpc,
                      isAutomationActive,
                      automationTypeIcon,
                      arpcColor,
                      arpcLabel,
                    } = automation;

                    return (
                      <TableRow
                        key={id}
                        href={
                          isMobile
                            ? `/analytics/${id}`
                            : `/automations/edit/${id}`
                        }
                      >
                        <TableCell className="w-[20%]">{name}</TableCell>
                        <TableCell className="text-center">
                          <Badge
                            color={
                              isAutomationActive
                                ? "lime"
                                : sent > 0
                                  ? "violet"
                                  : "sky"
                            }
                          >
                            {isAutomationActive ? (
                              <PlayCircleIcon className="h-5 w-5" />
                            ) : sent > 0 ? (
                              <PauseIcon className="h-5 w-5" />
                            ) : (
                              <PencilSquareIcon className="h-5 w-5" />
                            )}
                            <span>
                              {isAutomationActive
                                ? "Live"
                                : sent > 0
                                  ? "Inactive"
                                  : "Draft"}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Tooltip
                            content={
                              mappingAutomationTypeWithName[automationType] ??
                              "API"
                            }
                          >
                            <Icon
                              source={automationTypeIcon}
                              color="base"
                              accessibilityLabel="Automatisation"
                            />
                          </Tooltip>
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell className="text-center">
                              {updatedAt.toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "numeric",
                              })}
                            </TableCell>
                            <TableCell className="text-center">
                              {sent === undefined ? undefined : (
                                <>
                                  {sent || "-"}

                                  {delivered && delivered > 0 ? (
                                    <div className="text-xxs font-bold leading-none">
                                      {(
                                        (Math.min(read, delivered) /
                                          delivered) *
                                        100
                                      ).toFixed(0)}
                                      % read
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </TableCell>
                            {hasTurnover && (
                              <>
                                <TableCell className="text-center">
                                  {delivered === undefined ? undefined : (
                                    <>
                                      {delivered
                                        ? attributedRevenueShopify
                                          ? attributedRevenueShopify.toFixed(
                                              0,
                                            ) + "€"
                                          : externalTurnover > 0
                                            ? externalTurnover.toFixed(0) + "€"
                                            : "-"
                                        : "-"}
                                      {delivered &&
                                      attributedOrdersShopify > 0 ? (
                                        <div className="text-xxs font-bold leading-none">
                                          {attributedOrdersShopify} sale
                                          {attributedOrdersShopify > 1
                                            ? "s"
                                            : ""}
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {delivered === undefined ? undefined : (
                                    <>
                                      {arpc !== "-" && arpc > 0 ? (
                                        <Badge color={arpcColor}>
                                          {arpcLabel}
                                        </Badge>
                                      ) : (
                                        "-"
                                      )}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {delivered === undefined ? undefined : (
                                    <>
                                      {arpc > 0 ? `${arpc} €` : "-"}
                                      {arpc > 0 && (
                                        <div className="text-xxs font-bold leading-none">
                                          per conversation
                                        </div>
                                      )}
                                    </>
                                  )}
                                </TableCell>
                              </>
                            )}
                            <TableCell className="text-center">
                              <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                                <AutomationActionsDropdown
                                  automation={automation}
                                  customerId={user.customerId}
                                  setListData={setListData}
                                />
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                heading="You have no automations"
                action={async () => {
                  if (customer.isSubscribed) {
                    navigate("/automations/new");
                  }
                }}
                icon={<PlusIcon />}
                actionText="Create"
                img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              />
            )}
          </>
        )}
        {!search && (
          <div className="flex justify-start my-4">
            <Button
              color="secondaryButton"
              onClick={getPreviousPage}
              disabled={!data?.hasPreviousPage}
            >
              <ArrowLeftIcon />
              Previous
            </Button>
            <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
              {pageNumber}
            </Badge>
            <Button
              color="secondaryButton"
              onClick={getNextPage}
              disabled={!data?.hasNextPage}
            >
              Next
              <ArrowRightIcon />
            </Button>
          </div>
        )}
      </OverflowFrame>

      <DialogArchive
        customerId={user.customerId}
        listData={listData}
        setListData={setListData}
        mappingAutomationTypeWithField={mappingAutomationTypeWithField}
      />

      <ShareModal
        campaign={listData.selectedCampaign}
        scenarioId={listData.selectedCampaign?.scenario.id}
        isModalShareOpen={listData.isModalShareOpen}
        setIsModalShareOpen={updateIsModalShareOpen}
        isAutomation={true}
      />

      <DialogDuplicate
        customerId={user.customerId}
        listData={listData}
        setListData={setListData}
      />
    </>
  );
};
export default AutomationsPage;
